// src/pages/Comparison.tsx
import React, { useMemo, useState } from "react";
import {
  Box,
  Grid,
  Card,
  Typography,
  Stack,
  FormControl,
  Select,
  MenuItem,
  OutlinedInput,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Slider,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  ReferenceArea,
  ReferenceLine,
} from "recharts";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Tooltip as MuiTooltip } from "@mui/material";
import { ScatterChart, Scatter, ZAxis } from "recharts";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";

// ---------------- Config ----------------
const CHART_HEIGHT = { xs: 260, sm: 300, md: 340 };

// ---------------- Types ----------------
interface DataPoint {
  year: number;
  gdp: number;
  inflation: number;
  population: number; // in millions
  employment: number; // in millions
  financialLiteracy: number;
  educationLiteracy: number;
  smartphonePenetration: number;
}
type Country = string;

// ---------------- Sample Data (5 countries: 2018-2028) ----------------
const COUNTRY_DATA: Record<Country, DataPoint[]> = {
  India: [
    { year: 2018, gdp: 2.7, inflation: 3.6, population: 1352, employment: 450, financialLiteracy: 25, educationLiteracy: 74, smartphonePenetration: 25 },
    { year: 2019, gdp: 2.9, inflation: 3.7, population: 1366, employment: 455, financialLiteracy: 26, educationLiteracy: 75, smartphonePenetration: 30 },
    { year: 2020, gdp: 2.6, inflation: 6.2, population: 1380, employment: 460, financialLiteracy: 26, educationLiteracy: 75, smartphonePenetration: 35 },
    { year: 2021, gdp: 3.1, inflation: 5.1, population: 1393, employment: 465, financialLiteracy: 27, educationLiteracy: 76, smartphonePenetration: 40 },
    { year: 2022, gdp: 3.5, inflation: 6.2, population: 1406, employment: 470, financialLiteracy: 27, educationLiteracy: 76, smartphonePenetration: 45 },
    { year: 2023, gdp: 3.8, inflation: 5.0, population: 1420, employment: 475, financialLiteracy: 28, educationLiteracy: 77, smartphonePenetration: 50 },
    { year: 2024, gdp: 4.1, inflation: 4.8, population: 1438, employment: 480, financialLiteracy: 29, educationLiteracy: 78, smartphonePenetration: 55 },
    { year: 2025, gdp: 4.4, inflation: 4.5, population: 1452, employment: 485, financialLiteracy: 30, educationLiteracy: 78, smartphonePenetration: 60 },
    { year: 2026 , gdp: 4.8, inflation: 4.3, population: 1464, employment: 490, financialLiteracy: 31, educationLiteracy: 79, smartphonePenetration: 65 },
    { year: 2027, gdp: 5.2, inflation: 4.1, population: 1474, employment: 495, financialLiteracy: 32, educationLiteracy: 80, smartphonePenetration: 70 },
    { year: 2028, gdp: 5.6, inflation: 4.0, population: 1483, employment: 500, financialLiteracy: 33, educationLiteracy: 81, smartphonePenetration: 75 },
  ],
  USA: [
    { year: 2018, gdp: 20.5, inflation: 2.4, population: 327, employment: 155, financialLiteracy: 63, educationLiteracy: 99, smartphonePenetration: 70 },
    { year: 2019, gdp: 21.4, inflation: 1.8, population: 329, employment: 157, financialLiteracy: 64, educationLiteracy: 99, smartphonePenetration: 72 },
    { year: 2020, gdp: 20.9, inflation: 1.2, population: 331, employment: 158, financialLiteracy: 64, educationLiteracy: 99, smartphonePenetration: 75 },
    { year: 2021, gdp: 23.0, inflation: 4.7, population: 332, employment: 160, financialLiteracy: 65, educationLiteracy: 99, smartphonePenetration: 78 },
    { year: 2022, gdp: 26.0, inflation: 1.2, population: 333, employment: 160, financialLiteracy: 65, educationLiteracy: 99, smartphonePenetration: 80 },
    { year: 2023, gdp: 27.7, inflation: 4.7, population: 334, employment: 162, financialLiteracy: 66, educationLiteracy: 99, smartphonePenetration: 82 },
    { year: 2024, gdp: 29.2, inflation: 3.8, population: 335, employment: 165, financialLiteracy: 67, educationLiteracy: 99, smartphonePenetration: 84 },
    { year: 2025, gdp: 29.8, inflation: 3.5, population: 336, employment: 166, financialLiteracy: 67, educationLiteracy: 99, smartphonePenetration: 86 },
    { year: 2026, gdp: 30.4, inflation: 3.3, population: 337, employment: 167, financialLiteracy: 68, educationLiteracy: 99, smartphonePenetration: 88 },
    { year: 2027, gdp: 31.0, inflation: 3.2, population: 338, employment: 168, financialLiteracy: 68, educationLiteracy: 99, smartphonePenetration: 90 },
    { year: 2028, gdp: 31.7, inflation: 3.0, population: 339, employment: 169, financialLiteracy: 69, educationLiteracy: 99, smartphonePenetration: 92 },
  ],
  China: [
    { year: 2018, gdp: 13.6, inflation: 2.1, population: 1395, employment: 750, financialLiteracy: 36, educationLiteracy: 96, smartphonePenetration: 50 },
    { year: 2019, gdp: 14.3, inflation: 2.9, population: 1398, employment: 755, financialLiteracy: 37, educationLiteracy: 96, smartphonePenetration: 55 },
    { year: 2020, gdp: 14.7, inflation: 2.5, population: 1400, employment: 760, financialLiteracy: 38, educationLiteracy: 96, smartphonePenetration: 60 },
    { year: 2021, gdp: 17.7, inflation: 1.8, population: 1402, employment: 765, financialLiteracy: 38, educationLiteracy: 97, smartphonePenetration: 65 },
    { year: 2022, gdp: 18.0, inflation: 2.5, population: 1412, employment: 770, financialLiteracy: 39, educationLiteracy: 97, smartphonePenetration: 68 },
    { year: 2023, gdp: 18.7, inflation: 1.8, population: 1413, employment: 775, financialLiteracy: 40, educationLiteracy: 97, smartphonePenetration: 70 },
    { year: 2024, gdp: 19.2, inflation: 2.0, population: 1412, employment: 780, financialLiteracy: 42, educationLiteracy: 97, smartphonePenetration: 72 },
    { year: 2025, gdp: 20.1, inflation: 2.1, population: 1409, employment: 785, financialLiteracy: 43, educationLiteracy: 97, smartphonePenetration: 74 },
    { year: 2026, gdp: 21.0, inflation: 2.0, population: 1405, employment: 790, financialLiteracy: 44, educationLiteracy: 97, smartphonePenetration: 76 },
    { year: 2027, gdp: 21.9, inflation: 1.9, population: 1401, employment: 795, financialLiteracy: 45, educationLiteracy: 97, smartphonePenetration: 78 },
    { year: 2028, gdp: 22.7, inflation: 1.8, population: 1396, employment: 800, financialLiteracy: 46, educationLiteracy: 97, smartphonePenetration: 80 },
  ],
  Germany: [
    { year: 2018, gdp: 4.0, inflation: 1.9, population: 83, employment: 44, financialLiteracy: 66, educationLiteracy: 99, smartphonePenetration: 75 },
    { year: 2019, gdp: 4.2, inflation: 1.5, population: 83, employment: 44.5, financialLiteracy: 67, educationLiteracy: 99, smartphonePenetration: 78 },
    { year: 2020, gdp: 3.8, inflation: 0.5, population: 83, employment: 43.8, financialLiteracy: 67, educationLiteracy: 99, smartphonePenetration: 80 },
    { year: 2021, gdp: 4.3, inflation: 2.8, population: 83, employment: 44.2, financialLiteracy: 68, educationLiteracy: 99, smartphonePenetration: 82 },
    { year: 2022, gdp: 4.6, inflation: 3.1, population: 83, employment: 44.6, financialLiteracy: 69, educationLiteracy: 99, smartphonePenetration: 84 },
    { year: 2023, gdp: 4.8, inflation: 2.5, population: 84, employment: 44.9, financialLiteracy: 70, educationLiteracy: 99, smartphonePenetration: 86 },
    { year: 2024, gdp: 5.0, inflation: 2.3, population: 84, employment: 45.2, financialLiteracy: 71, educationLiteracy: 99, smartphonePenetration: 87 },
    { year: 2025, gdp: 5.3, inflation: 2.2, population: 84, employment: 45.5, financialLiteracy: 72, educationLiteracy: 99, smartphonePenetration: 88 },
    { year: 2026, gdp: 5.5, inflation: 2.0, population: 84, employment: 45.8, financialLiteracy: 73, educationLiteracy: 99, smartphonePenetration: 89 },
    { year: 2027, gdp: 5.7, inflation: 1.9, population: 84, employment: 46.0, financialLiteracy: 74, educationLiteracy: 99, smartphonePenetration: 90 },
    { year: 2028, gdp: 5.9, inflation: 1.8, population: 84, employment: 46.2, financialLiteracy: 75, educationLiteracy: 99, smartphonePenetration: 91 },
  ],
  Brazil: [
    { year: 2018, gdp: 2.1, inflation: 3.7, population: 209, employment: 95, financialLiteracy: 34, educationLiteracy: 92, smartphonePenetration: 55 },
    { year: 2019, gdp: 2.2, inflation: 3.7, population: 211, employment: 95.5, financialLiteracy: 35, educationLiteracy: 92, smartphonePenetration: 58 },
    { year: 2020, gdp: 1.8, inflation: 3.7, population: 212, employment: 94.8, financialLiteracy: 35, educationLiteracy: 92, smartphonePenetration: 60 },
    { year: 2021, gdp: 1.9, inflation: 8.3, population: 213, employment: 95.0, financialLiteracy: 36, educationLiteracy: 92, smartphonePenetration: 62 },
    { year: 2022, gdp: 2.0, inflation: 9.3, population: 214, employment: 95.2, financialLiteracy: 37, educationLiteracy: 92, smartphonePenetration: 64 },
    { year: 2023, gdp: 2.1, inflation: 5.8, population: 215, employment: 95.6, financialLiteracy: 38, educationLiteracy: 92, smartphonePenetration: 66 },
    { year: 2024, gdp: 2.2, inflation: 4.5, population: 216, employment: 96.0, financialLiteracy: 39, educationLiteracy: 92, smartphonePenetration: 67 },
    { year: 2025, gdp: 2.3, inflation: 3.9, population: 217, employment: 96.3, financialLiteracy: 40, educationLiteracy: 93, smartphonePenetration: 68 },
    { year: 2026, gdp: 2.4, inflation: 3.5, population: 218, employment: 96.6, financialLiteracy: 41, educationLiteracy: 93, smartphonePenetration: 69 },
    { year: 2027, gdp: 2.5, inflation: 3.2, population: 219, employment: 96.9, financialLiteracy: 42, educationLiteracy: 93, smartphonePenetration: 70 },
    { year: 2028, gdp: 2.6, inflation: 3.0, population: 220, employment: 97.2, financialLiteracy: 43, educationLiteracy: 93, smartphonePenetration: 71 },
  ],
};

// ---------------- Colors and Spacing ----------------
const colorPalette = [
  "#3B82F6", // vibrant blue
  "#F59E0B", // rich amber
  "#10B981", // emerald green
  "#EF4444", // modern red
  "#8B5CF6", // soft purple
];
const countryColors: Record<Country, string> = {};
Object.keys(COUNTRY_DATA).forEach((c, i) => {
  countryColors[c] = colorPalette[i % colorPalette.length];
});

const percentFormatter = (value?: number, name?: string) => [
  `${value ?? 0}%`,
  name ?? "",
];

const safeFormatter = ((value: any, name: any) => [
  `${value ?? 0}%`,
  name ?? "",
]) as any;

const safeValueFormatter = ((value: any) => `${value ?? 0}%`) as any;

const countryGradients: Record<Country, [string, string]> = {
  India: ["#ebf858", "#ecf715"],     // blue → sky
  USA: ["#22C55E", "#14B8A6"],       // green → teal
  China: ["#EF4444", "#e51e1e"],     // emerald → soft green
  Germany:["#3B82F6", "#60A5FA"],   // blue,   // orange → soft red
  Brazil: ["#8B5CF6", "#6366F1"],    // purple → indigo
};

const SPACING = {
  cardPadding: { xs: 2, md: 3 },
  cardGap: 4,
  sectionGap: 4,
  headerMargin: 2,
};

// ---------------- Flag URLs ----------------
const FLAG_URLS: Record<string, string> = {
  India: "https://flagcdn.com/w320/in.png",
  USA: "https://flagcdn.com/w320/us.png",
  China: "https://flagcdn.com/w320/cn.png",
  Germany: "https://flagcdn.com/w320/de.png",
  Brazil: "https://flagcdn.com/w320/br.png",
};

const Comparison: React.FC = () => {
  const allCountries = Object.keys(COUNTRY_DATA);
  const [selectedCountries, setSelectedCountries] = useState<Country[]>([]);
  const [selectedYear, setSelectedYear] = useState<number | "All">("All");

  const [openModal, setOpenModal] = useState(false);
  const [modalData, setModalData] = useState<{ country: Country; data: DataPoint | null }>({
    country: "",
    data: null,
  });

const [focusCountry, setFocusCountry] = useState<string | null>(null);

const [xMetric, setXMetric] = useState<keyof DataPoint>("gdp");
const [yMetric, setYMetric] = useState<keyof DataPoint>("employment");

  const [gdpViewMode, setGdpViewMode] = useState<"Chart" | "Table">("Chart");
  const [pieMetric, setPieMetric] = useState<"financialLiteracy" | "smartphonePenetration">("financialLiteracy");

  const baseCountries = selectedCountries.length > 0 ? selectedCountries : allCountries;

  const generateInsight = (metric: "gdp" | "inflation" | "employment") => {
  const countries = chartCountries;

  if (metric === "gdp") {
    const latestValues = countries.map(c => {
      const data = COUNTRY_DATA[c];
      return { country: c, value: data[data.length - 1].gdp };
    });

    const top = latestValues.sort((a, b) => b.value - a.value)[0];

    return `${top.country} has the highest GDP among selected countries. Developing economies like India show faster growth rates, while developed economies remain stable and dominant.`;
  }

  if (metric === "inflation") {
    const values = countries.map(c => {
      const data = COUNTRY_DATA[c];
      return data.map(d => d.inflation);
    });

    const volatility = values.map(arr => Math.max(...arr) - Math.min(...arr));
    const maxVol = Math.max(...volatility);
    const volatileCountry = countries[volatility.indexOf(maxVol)];

    return `${volatileCountry} shows the most fluctuation in inflation, indicating economic instability during certain periods. Stable inflation trends suggest stronger monetary control.`;
  }

  if (metric === "employment") {
    return `Employment rates are relatively stable across developed nations, while developing economies show gradual improvement. Sudden dips may indicate economic shocks or crises.`;
  }

  return "";
};

const getAnomalies = (country: string, key: keyof DataPoint) => {
  const data = COUNTRY_DATA[country];
  const anomalies: number[] = [];

  for (let i = 1; i < data.length; i++) {
    const prev = data[i - 1][key] as number;
    const curr = data[i][key] as number;

    if (prev && curr) {
      const change = ((curr - prev) / prev) * 100;

      if (Math.abs(change) > 10) {
        anomalies.push(data[i].year);
      }
    }
  }

  return anomalies;
};

const chartCountries = focusCountry
  ? [focusCountry]
  : baseCountries;

  const theme = useTheme();

const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

const chartHeight = isMobile ? 220 : isTablet ? 280 : 340;

  const years = useMemo(() => {
    const s = new Set<number>();
    Object.values(COUNTRY_DATA).forEach((arr) => arr.forEach((d) => s.add(d.year)));
    return Array.from(s).sort((a, b) => a - b);
  }, []);

  const [infoOpen, setInfoOpen] = useState(false);
const [infoContent, setInfoContent] = useState("");



  // ---------- Education & Literacy (second row uses year filter as before) ----------
  const educationData = useMemo(() => {
    const year = selectedYear === "All" ? years[years.length - 1] : selectedYear;
    return chartCountries
      .map((c) => {
        const data = COUNTRY_DATA[c].find((d) => d.year === year);
        return { name: c, value: data ? data.educationLiteracy : 0 };
      })
      .sort((a, b) => b.value - a.value);
  }, [chartCountries, selectedYear, years]);

  // ---------- Full-range datasets (2018–2028), ONLY country filter applies ----------
  const buildFullRangeChartData = (key: keyof DataPoint) =>
    years.map((year) => {
      const row: any = { year };
      chartCountries.forEach((c) => {
        const dp = COUNTRY_DATA[c].find((d) => d.year === year);
        row[c] = dp ? dp[key] : null;
      });
      return row;
    });

  const gdpFullData = useMemo(() => buildFullRangeChartData("gdp"), [chartCountries, years]);

  const employmentRateFullData = useMemo(() => {
    return years.map((year) => {
      const row: any = { year };
      chartCountries.forEach((c) => {
        const dp = COUNTRY_DATA[c].find((d) => d.year === year);
        row[c] = dp ? Number(((dp.employment / dp.population) * 100).toFixed(2)) : null;
      });
      return row;
    });
  }, [chartCountries, years]);

  const MIN_VAL = 30;
const MAX_VAL = 60;

const normalizedEmploymentRateData = employmentRateFullData.map((row) => {
  const newRow: any = { year: row.year };
  chartCountries.forEach((c) => {
    const values = employmentRateFullData.map((r) => r[c]);
    const min = Math.min(...values);
    const max = Math.max(...values);
    // Linear mapping from [min, max] → [MIN_VAL, MAX_VAL]
    newRow[c] = max > min ? ((row[c] - min) / (max - min)) * (MAX_VAL - MIN_VAL) + MIN_VAL : MIN_VAL;
  });
  return newRow;
});

  const inflationFullData = useMemo(() => buildFullRangeChartData("inflation"), [chartCountries, years]);

  // ---------- Pie data (second row) ----------
  const pieData = useMemo(() => {
    const year = selectedYear === "All" ? years[years.length - 1] : selectedYear;
    return chartCountries.map((c) => {
      const data = COUNTRY_DATA[c].find((d) => d.year === year);
      return {
        name: c,
        value: data ? data[pieMetric] : 0,
      };
    });
  }, [chartCountries, selectedYear, pieMetric, years]);

const correlationData = useMemo(() => {
  const data: any[] = [];

  chartCountries.forEach((c) => {
    COUNTRY_DATA[c].forEach((d) => {
      data.push({
        country: c,
        x: d[xMetric],
        y: d[yMetric],
      });
    });
  });

  return data;
}, [chartCountries, xMetric, yMetric]);

const [showCorrelation, setShowCorrelation] = React.useState(false);

  // LabelList formatter (kept for future use)
  const percentLabelFormatter = (label: React.ReactNode): React.ReactNode => {
    if (typeof label === "number") return `${label}%`;
    if (typeof label === "string") {
      const n = Number(label);
      return isFinite(n) ? `${n}%` : label;
    }
    return label;
  };

  return (
    <Box p={{ xs: 2, md: 5 }} sx={{ bgcolor: "#f4f7fb", minHeight: "100vh" }}>
      {focusCountry && (
  <Button
    variant="outlined"
    size="small"
    onClick={() => setFocusCountry(null)}
    sx={{ mt: 2 }}
  >
    Reset Focus Mode ({focusCountry})
  </Button>
)}
      <Box textAlign="center" mb={4}>
        <Typography variant="h4" fontWeight={700} sx={{ background: "linear-gradient(90deg, #6f92e4, #3d5eb9)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
          Global Economic Comparison Dashboard
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" mt={1}>
          Compare GDP, Inflation, Employment, Education & Financial Literacy, Smartphone Penetration (2018–2028)
        </Typography>
      </Box>

      {/* Row 1: GDP, Employment Rate, Inflation Rate (2018–2028, uses country filter only) */}
      <Grid container spacing={4}>
        {/* GDP */}
        <Grid item xs={12} md={4}>
          <Card
            sx={{
              p: SPACING.cardPadding,
              borderRadius: 3,
              boxShadow: 4,
              transition: "0.3s",
              "&:hover": { boxShadow: 8, transform: "translateY(-4px)" },
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6" color="#0F5BD8" fontWeight={600}>
                GDP (USD) — 2018–2028
              </Typography>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <Select value={gdpViewMode} onChange={(e) => setGdpViewMode(e.target.value as "Chart" | "Table")}>
                  <MenuItem value="Chart">Chart</MenuItem>
                  <MenuItem value="Table">Table</MenuItem>
                </Select>
              </FormControl>
            </Stack>

            {gdpViewMode === "Chart" ? (
              <Box sx={{ position: "relative" }}>
                <ResponsiveContainer width="100%" height={chartHeight}>
                  <BarChart
  data={gdpFullData}
  onClick={(state: any) => {
    if (state?.activeLabel) {
      setFocusCountry(null);
    }
  }}
>
 <defs>
  <filter id="glow">
  <feGaussianBlur stdDeviation="3.5" result="coloredBlur"/>
  <feMerge>
    <feMergeNode in="coloredBlur"/>
    <feMergeNode in="SourceGraphic"/>
  </feMerge>
</filter>
  {chartCountries.map((c) => (
    <linearGradient
      key={c}
      id={`gradient-${c}`}
      x1="0"
      y1="0"
      x2="0"
      y2="1"
    >
      <stop offset="0%" stopColor={countryGradients[c][0]} />
      <stop offset="100%" stopColor={countryGradients[c][1]} />
    </linearGradient>
  ))}
</defs>
                    <CartesianGrid stroke="#eef2f5" />
                    <XAxis dataKey="year" />
                    <YAxis />
                    <RechartsTooltip
  contentStyle={{
    borderRadius: "10px",
    fontSize: "14px",
    padding: "8px 12px"
  }}
/>
                    <Legend verticalAlign="bottom" height={36} />

                    {/* Highlight predicted region */}
                    <ReferenceArea x1="2026" x2="2028" fill="#0F5BD8" fillOpacity={0.05} stroke="none" />
                    {chartCountries.map((c) =>
  getAnomalies(c, "gdp").map((year) => (
    <ReferenceLine
      key={`${c}-anomaly-${year}`}
      x={year}
      stroke="red"
      strokeDasharray="3 3"
      label={{
        value: year === 2020 ? "COVID Impact" : "Shock",
        position: "top",
        fill: "red",
        fontSize: 10,
      }}
    />
  ))
)} 
                    {/* Bars for each country */}
                    {chartCountries.map((c) => (
                     <Bar
                      filter="url(#glow)"
  key={c}
  dataKey={c}
  fill={`url(#gradient-${c})`}
  onClick={() => setFocusCountry(c)}
  cursor="pointer"
  opacity={focusCountry && focusCountry !== c ? 0.2 : 1}
  isAnimationActive={true}
  animationDuration={1200}
  animationEasing="ease-out"
/>
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            ) : (
              // Table view
              <Box sx={{ overflowX: "auto" }}>
                <Table size="small" sx={{ minWidth: 900 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell
  sx={{
    position: "sticky",
    left: 0,
    background: "#fff",
    zIndex: 2,
    fontWeight: 600
  }}
>Year</TableCell>
                      {chartCountries.map((c) => (
                        <TableCell
                          key={c}
                          sx={{
                            fontWeight: "bold",
                            textAlign: "center",
                            whiteSpace: "nowrap",
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                            <img src={FLAG_URLS[c]} alt={`${c} flag`} style={{ width: 24, height: 16, objectFit: "cover" }} />
                            <span>{c}</span>
                          </Box>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {Array.from({ length: 11 }, (_, i) => 2018 + i).map((year, index) => (
                      <TableRow key={year}>
                        <TableCell
                          sx={{
                            textAlign: "center",
                            fontWeight: year >= 2026 ? 600 : 400,
                            background: year >= 2026 ? "rgba(15,91,216,0.05)" : "transparent",
                            borderLeft: year === 2026 ? "2px solid #999" : "1px solid #ddd",
                          }}
                        >
                          {year}
                        </TableCell>
                        {chartCountries.map((country) => {
                          const entry = COUNTRY_DATA[country].find((d) => d.year === year);
                          const prevEntry = index > 0 ? COUNTRY_DATA[country].find((d) => d.year === 2018 + index - 1) : null;

                          let change: string | null = null;
                          if (entry && prevEntry && prevEntry.gdp > 0) {
                            const diff = ((entry.gdp - prevEntry.gdp) / prevEntry.gdp) * 100;
                            change = diff.toFixed(1);
                          }

                          return (
                            <TableCell
                              key={`${country}-gdp-${year}`}
                              align="center"
                              sx={{
                                background: year >= 2026 ? "rgba(15,91,216,0.05)" : "#f8f9fa",
                                borderLeft: year === 2026 ? "2px solid #999" : "1px solid #ddd",
                              }}
                            >
                              {entry ? (
                                <>
                                  {entry.gdp.toLocaleString(undefined, { maximumFractionDigits: 1 })}
                                  {change !== null && (
                                    <Typography
                                      component="span"
                                      sx={{
                                        ml: 0.5,
                                        fontSize: "0.75rem",
                                        color: Number(change) > 0 ? "green" : Number(change) < 0 ? "red" : "gray",
                                        fontWeight: 600,
                                      }}
                                    >
                                      {Number(change) > 0 ? `+${change}%` : `${change}%`}
                                    </Typography>
                                  )}
                                </>
                              ) : (
                                "—"
                              )}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
          </Card>
        </Grid>

       {/* Employment Rate */}
<Grid item xs={12} md={4}>
  <Card
    sx={{
              p: SPACING.cardPadding,
              borderRadius: 3,
              boxShadow: 4,
              transition: "0.3s",
              "&:hover": { boxShadow: 8, transform: "translateY(-4px)" },
            }}
  >
    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
  <Typography
    variant="h6"
    sx={{
      fontWeight: 600,
      color: "#0F5BD8",
    }}
  >
    Employment Rate (%) — 2018–2028
  </Typography>

<MuiTooltip title="What does this mean?" arrow>
  <InfoOutlinedIcon
    onClick={() => {
      setInfoContent(
        "Employment rate shows the percentage of people working. Stable trends suggest strong economies, while dips may indicate unemployment or economic stress. Developed nations tend to have consistent rates."
      );
      setInfoOpen(true);
    }}
    sx={{
      cursor: "pointer",
      color: "#6B7280",
      fontSize: 22,
      transition: "0.2s",
      "&:hover": {
        color: "#0F5BD8",
        transform: "scale(1.1)",
      },
    }}
  />
</MuiTooltip>
</Stack>
    <ResponsiveContainer width="100%" height={chartHeight}>
      <LineChart data={employmentRateFullData}>
  <defs>
    <filter id="glow">
  <feGaussianBlur stdDeviation="3.5" result="coloredBlur"/>
  <feMerge>
    <feMergeNode in="coloredBlur"/>
    <feMergeNode in="SourceGraphic"/>
  </feMerge>
</filter>
  {chartCountries.map((c) => (
    <linearGradient
      key={c}
      id={`gradient-${c}`}
      x1="0"
      y1="0"
      x2="0"
      y2="1"
    >
      <stop offset="0%" stopColor={countryGradients[c][0]} />
      <stop offset="100%" stopColor={countryGradients[c][1]} />
    </linearGradient>
  ))}
</defs>
        <CartesianGrid stroke="#eef2f5" />
        <XAxis dataKey="year" />
        <YAxis
          domain={[30, 60]} // <-- zoom in between 30% and 60%
          tickFormatter={(v) => `${v}%`}
        />
        <RechartsTooltip
         formatter={safeFormatter}
          labelFormatter={(label) => `Year: ${label}`}
        />
        <Legend verticalAlign="bottom" height={36} />
        <ReferenceArea x1={2026} x2={2028} fill="#E5E7EB" fillOpacity={0.3} />
        <ReferenceLine
          x={2025.5}
          stroke="#9CA3AF"
          strokeDasharray="3 3"
          label={{ value: "Predicted", position: "top", fill: "#6B7280", fontSize: 12 }}
        />
        {chartCountries.map((c) => (
          <Line
          filter="url(#glow)"
  key={c}
  dataKey={c}
  stroke={`url(#gradient-${c})`}
  strokeWidth={4}
  type="monotone"
  dot={false}
  isAnimationActive={true}
  animationDuration={1200}
  animationEasing="ease-in-out"
  strokeLinecap="round"
  name={`${c}`}
  opacity={focusCountry && focusCountry !== c ? 0.2 : 1}
  style={{ cursor: "pointer" }}
  onClick={() => setFocusCountry(c)}
/>
        ))}
      </LineChart>
    </ResponsiveContainer>
  </Card>
</Grid>

        {/* Inflation Rate */}
        <Grid item xs={12} md={4}>
          <Card
            sx={{
  p: SPACING.cardPadding,
  borderRadius: 4,
  boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
  backdropFilter: "blur(10px)",
  background: "linear-gradient(135deg, #ffffff, #f9fbff)",
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "translateY(-6px)",
    boxShadow: "0 12px 30px rgba(0,0,0,0.12)",
  },
}}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
  <Typography
    variant="h6"
    sx={{
      fontWeight: 600,
      color: "#0F5BD8",
    }}
  >
    Inflation Rate (%) — 2018–2028
  </Typography>

  <MuiTooltip title="What does this mean?" arrow>
    <InfoOutlinedIcon
      onClick={() => {
        setInfoContent(
          "Inflation measures how quickly prices increase over time. Moderate inflation is healthy for growth, but high or volatile inflation reduces purchasing power and economic stability."
        );
        setInfoOpen(true);
      }}
      sx={{
        cursor: "pointer",
        color: "#6B7280",
        fontSize: 22,
        transition: "0.2s",
        "&:hover": {
          color: "#0F5BD8",
          transform: "scale(1.1)",
        },
      }}
    />
  </MuiTooltip>
</Stack>
            <ResponsiveContainer width="100%" height={chartHeight}>
              <LineChart data={inflationFullData}>
  <defs>
    <filter id="glow">
  <feGaussianBlur stdDeviation="3.5" result="coloredBlur"/>
  <feMerge>
    <feMergeNode in="coloredBlur"/>
    <feMergeNode in="SourceGraphic"/>
  </feMerge>
</filter>
  {chartCountries.map((c) => (
    <linearGradient
      key={c}
      id={`pieGradient-${c}`}
      x1="0"
      y1="0"
      x2="0"
      y2="1"
    >
      <stop offset="0%" stopColor={countryGradients[c][0]} />
      <stop offset="100%" stopColor={countryGradients[c][1]} />
    </linearGradient>
  ))}
</defs>
                <CartesianGrid stroke="#eef2f5" />
                <XAxis dataKey="year" />
                <YAxis tickFormatter={safeValueFormatter} />
                <RechartsTooltip formatter={safeFormatter} labelFormatter={(label) => `Year: ${label}`} />
                <Legend verticalAlign="bottom" height={36} />
                <ReferenceArea x1={2026} x2={2028} fill="#E5E7EB" fillOpacity={0.3} />
                <ReferenceLine
                  x={2025.5}
                  stroke="#9CA3AF"
                  strokeDasharray="3 3"
                  label={{ value: "Predicted", position: "top", fill: "#6B7280", fontSize: 12 }}
                />
                {chartCountries.map((c) => (
          <Line
          filter="url(#glow)"
  key={c}
  dataKey={c}
  stroke={`url(#gradient-${c})`}
  strokeWidth={4}
  type="monotone"
  dot={false}
  isAnimationActive={true}
  animationDuration={1200}
  animationEasing="ease-in-out"
  strokeLinecap="round"
  name={`${c}`}
  opacity={focusCountry && focusCountry !== c ? 0.2 : 1}
  style={{ cursor: "pointer" }}
  onClick={() => setFocusCountry(c)}
/>
        ))}
              </LineChart>
            </ResponsiveContainer>
          </Card>
        </Grid>
      </Grid>

      {/* Row 2: Education & Literacy Ranking + Dynamic Pie */}
      <Grid container spacing={4} mt={0}>
        {/* Education & Literacy Ranking */}
        <Grid item xs={12} md={6}>
          <Card
          sx={{
  p: SPACING.cardPadding,
  borderRadius: 4,
  boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
  backdropFilter: "blur(10px)",
  background: "linear-gradient(135deg, #ffffff, #f9fbff)",
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "translateY(-6px)",
    boxShadow: "0 12px 30px rgba(0,0,0,0.12)",
  },
}}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
  <Typography
    variant="h6"
    sx={{
      fontWeight: 600,
      color: "#0F5BD8",
    }}
  >
    Education & Literacy Ranking
  </Typography>

  <MuiTooltip title="What does this mean?" arrow>
    <InfoOutlinedIcon
      onClick={() => {
        setInfoContent(
          "Education literacy reflects the percentage of people who can read and write. Higher literacy rates are linked to better economic opportunities, productivity, and overall development."
        );
        setInfoOpen(true);
      }}
      sx={{
        cursor: "pointer",
        color: "#6B7280",
        fontSize: 22,
        transition: "0.2s",
        "&:hover": {
          color: "#0F5BD8",
          transform: "scale(1.1)",
        },
      }}
    />
  </MuiTooltip>
</Stack>

            <Box sx={{ width: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}>
              <ResponsiveContainer width="98%" height={365}>
                <BarChart
                  layout="vertical"
                  data={[
                    educationData.find((d) => d.name === "USA"),
                    educationData.find((d) => d.name === "Germany"),
                    educationData.find((d) => d.name === "China"),
                    educationData.find((d) => d.name === "Brazil"),
                    { name: "Global", value: 86 },
                    educationData.find((d) => d.name === "India"),
                  ].filter((d): d is { name: string; value: number } => Boolean(d))}
                  margin={{ top: 20, right: 60, left: 60, bottom: 20 }}
                  barCategoryGap="20%"
                >
                  <defs>
  {["India", "USA", "China", "Germany", "Brazil"].map((c) => (
    <linearGradient
      key={c}
      id={`eduGradient-${c}`}
      x1="0"
      y1="0"
      x2="1"
      y2="0"
    >
      <stop offset="0%" stopColor={countryGradients[c][0]} />
      <stop offset="100%" stopColor={countryGradients[c][1]} />
    </linearGradient>
  ))}
</defs>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" domain={[0, 100]} hide={false} />
                  <YAxis
                    type="category"
                    dataKey="name"
                    axisLine={false}
                    tickLine={false}
                    width={100}
                    tick={({ x, y, payload }) => {
                      const flag = FLAG_URLS[payload.value];
                      const isGlobal = payload.value === "Global";

                      return (
                        <g transform={`translate(${(Number(x) || 0) - 40},${Number(y) || 0})`}>
                          {!isGlobal ? (
                            <>
                              <rect x={-65} y={-10} width={60} height={20} rx={4} fill="none" stroke="#ddd" />
                              <image href={flag} x={-65} y={-10} width={60} height={20} clipPath="inset(0 round 4px)" />
                            </>
                          ) : (
                            <>
                              <rect x={-65} y={-10} width={60} height={25} rx={4} fill="#e5e7eb" />
                              <text x={-35} y={4} fontSize={22} textAnchor="middle" dominantBaseline="middle">
                                🌍
                              </text>
                            </>
                          )}

                          <text x={0} y={0} dy={4} textAnchor="start" fill={isGlobal ? "#555" : "#111"} fontSize={10} fontWeight={600}>
                            {payload.value}
                          </text>
                        </g>
                      );
                    }}
                  />
                  <RechartsTooltip formatter={safeValueFormatter} />
                  <Bar dataKey="value" radius={[8, 8, 8, 8]} barSize={25}
                  onClick={(data: any) => {
  if (data?.name) {
    setFocusCountry(data.name);
  }
}}>         
                    {[
                      educationData.find((d) => d.name === "USA"),
                      educationData.find((d) => d.name === "China"),
                      educationData.find((d) => d.name === "Germany"),
                      educationData.find((d) => d.name === "Brazil"),
                      { name: "Global", value: 86 },
                      educationData.find((d) => d.name === "India"),
                    ]
                      .filter((d): d is { name: string; value: number } => Boolean(d))
                        .map((entry) => (
  <Cell
    key={entry.name}
    fill={
      entry.name === "Global"
        ? "#9CA3AF"
        : `url(#eduGradient-${entry.name})`
    }
  />
))}
                      
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Card>
        </Grid>

        {/* Dynamic Pie Chart (Title-as-dropdown + Year Slider) */}
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              width: "100%",
  p: SPACING.cardPadding,
  borderRadius: 4,
  boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
  backdropFilter: "blur(10px)",
  background: "linear-gradient(135deg, #ffffff, #f9fbff)",
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "translateY(-6px)",
    boxShadow: "0 12px 30px rgba(0,0,0,0.12)",
  },
}}
          >
            {/* Header: title acts as the metric dropdown; year uses a slider */}
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              flexWrap="nowrap"
              mb={2}
              gap={2}
              sx={{ minHeight: 58 }}
            >
              {/* Title-as-Dropdown (metric selector) */}
              <FormControl size="small" sx={{ minWidth: 240 }}>
                <Select
                  value={pieMetric}
                  onChange={(e) =>
                    setPieMetric(e.target.value as "financialLiteracy" | "smartphonePenetration")
                  }
                  input={<OutlinedInput />}
                  displayEmpty
                  sx={{
                    "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                    "& .MuiSelect-select": {
                      fontSize: "1.25rem",
                      fontWeight: 700,
                      color: "#0F5BD8",
                      p: 0,
                    },
                  }}
                  renderValue={(val) =>
                    val === "financialLiteracy" ? "Financial Literacy (%)" : "Smartphone Penetration (%)"
                  }
                >
                  <MenuItem value="financialLiteracy">Financial Literacy (%)</MenuItem>
                  <MenuItem value="smartphonePenetration">Smartphone Penetration (%)</MenuItem>
                </Select>
              </FormControl>

              {/* Year Slider (2018–2028) */}
              <Box sx={{ minWidth: 260, px: 1 }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Typography fontWeight={600} mb={1} sx={{ whiteSpace: "nowrap" }}>
                    Year
                  </Typography>
                  <Slider
                    min={2018}
                    max={2028}
                    step={1}
                    value={typeof selectedYear === "number" ? selectedYear : 2028}
                    onChange={(_, v) => setSelectedYear(Number(v))}
                    marks={[
                      { value: 2018, label: "2018" },
                      { value: 2020, label: "2020" },
                      { value: 2022, label: "2022" },
                      { value: 2024, label: "2024" },
                      { value: 2026, label: "2026" },
                      { value: 2028, label: "2028" },
                    ]}
                    valueLabelDisplay="auto"
                    sx={{ width: "100%" }}
                  />
                </Stack>
              </Box>
            </Stack>

            {/* Reactive Pie Chart */}
            <ResponsiveContainer width="100%" height={chartHeight}>
              <PieChart>
                <RechartsTooltip formatter={safeValueFormatter} />
                <Legend verticalAlign="bottom" wrapperStyle={{ fontSize: "13px" }} />
                <Pie
                  data={pieData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius="90%"
                  innerRadius="20%"
                  labelLine={false}
                  isAnimationActive
                  animationBegin={0}
                  animationDuration={800}
                  onClick={(data: any) => {
  if (data?.name) {
    setFocusCountry(data.name);
  }
}}
                >

                  <defs>
                    <filter id="glow">
  <feGaussianBlur stdDeviation="3.5" result="coloredBlur"/>
  <feMerge>
    <feMergeNode in="coloredBlur"/>
    <feMergeNode in="SourceGraphic"/>
  </feMerge>
</filter>
  {chartCountries.map((c) => (
    <linearGradient
      key={c}
      id={`pieGradient-${c}`}
      x1="0"
      y1="0"
      x2="0"
      y2="1"
    >
      <stop offset="0%" stopColor={countryGradients[c][0]} />
      <stop offset="100%" stopColor={countryGradients[c][1]} />
    </linearGradient>
  ))}
</defs>

                  {pieData.map((entry, index) => (
                   <Cell
  key={`cell-${index}`}
  fill={`url(#pieGradient-${entry.name})`}
/>
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </Grid>
      </Grid>

      {/* Data Table (GDP & Population with Predicted Section) */}
      <Box mt={4}>
        <Grid container>
          <Grid item xs={12}>
            <Card
              sx={{
  p: SPACING.cardPadding,
  borderRadius: 4,
  boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
  backdropFilter: "blur(10px)",
  background: "linear-gradient(135deg, #ffffff, #f9fbff)",
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "translateY(-6px)",
    boxShadow: "0 12px 30px rgba(0,0,0,0.12)",
  },
}}
            >
              <Typography variant="h6" fontWeight={600} mb={1}>
                GDP & Population (2018–2028)
              </Typography>

              <Box sx={{ overflowX: "auto" }}>
                <Table sx={{ minWidth: 1500, position: "relative" }}>
                  <TableHead>
                    {/* Predicted Header Row */}
                    <TableRow>
                      <TableCell colSpan={3}></TableCell>
                      {Array.from({ length: 8 }).map((_, i) => (
                        <TableCell key={`empty-${i}`}></TableCell>
                      ))}
                      <TableCell
                        key="predicted-header"
                        align="center"
                        colSpan={3}
                        sx={{
                          fontWeight: 700,
                          textAlign: "center",
                          borderBottom: "none",
                          fontSize: "1rem",
                          color: "#7ca9abff",
                        }}
                      >
                        Predicted
                      </TableCell>
                    </TableRow>

                    {/* Year Labels */}
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, textAlign: "center", whiteSpace: "nowrap" }}>
                        Country
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}></TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Indicator</TableCell>

                      {Array.from({ length: 11 }, (_, i) => 2018 + i).map((year) => (
                        <TableCell
                          key={year}
                          sx={{
                            fontWeight: 600,
                            textAlign: "center",
                            whiteSpace: "nowrap",
                            borderLeft: year === 2026 ? "2px solid #999" : "1px solid #ddd",
                            background: year >= 2026 ? "rgba(15,91,216,0.05)" : "transparent",
                          }}
                        >
                          {year}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {Object.keys(COUNTRY_DATA).map((country) => (
                      <React.Fragment key={country}>
                        {/* GDP Row */}
                        <TableRow hover>
                          <TableCell rowSpan={2}>
                            <Typography fontWeight={600}>{country}</Typography>
                          </TableCell>
                          <TableCell rowSpan={2}>
                            <img src={FLAG_URLS[country]} alt={`${country} flag`} style={{ width: 28, height: 18, borderRadius: 3 }} />
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>GDP (T USD)</TableCell>

                          {Array.from({ length: 11 }, (_, i) => 2018 + i).map((year, index) => {
                            const entry = COUNTRY_DATA[country].find((d) => d.year === year);
                            const prevEntry = index > 0 ? COUNTRY_DATA[country].find((d) => d.year === 2018 + index - 1) : null;

                            let change: string | null = null;
                            if (entry && prevEntry && prevEntry.gdp > 0) {
                              const diff = ((entry.gdp - prevEntry.gdp) / prevEntry.gdp) * 100;
                              change = diff.toFixed(1);
                            }

                            return (
                              <TableCell
                                key={`${country}-gdp-${year}`}
                                align="center"
                                sx={{
                                  background: year >= 2026 ? "rgba(15,91,216,0.05)" : "#f8f9fa",
                                  borderLeft: year === 2026 ? "2px solid #999" : "1px solid #ddd",
                                }}
                              >
                                {entry ? (
                                  <>
                                    {entry.gdp.toLocaleString(undefined, { maximumFractionDigits: 1 })}
                                    {change !== null && (
                                      <Typography
                                        component="span"
                                        sx={{
                                          ml: 0.5,
                                          fontSize: "0.75rem",
                                          color: Number(change) > 0 ? "green" : Number(change) < 0 ? "red" : "gray",
                                          fontWeight: 600,
                                        }}
                                      >
                                        {Number(change) > 0 ? `(+${change}%)` : `(${change}%)`}
                                      </Typography>
                                    )}
                                  </>
                                ) : (
                                  "—"
                                )}
                              </TableCell>
                            );
                          })}
                        </TableRow>

                        {/* Population Row */}
                        <TableRow hover>
                          <TableCell sx={{ fontWeight: 600 }}>Population (M)</TableCell>
                          {Array.from({ length: 11 }, (_, i) => 2018 + i).map((year) => {
                            const entry = COUNTRY_DATA[country].find((d) => d.year === year);
                            return (
                              <TableCell
                                key={`${country}-pop-${year}`}
                                align="center"
                                sx={{
                                  borderLeft: year === 2026 ? "2px solid #999" : "1px solid #ddd",
                                  background: year >= 2026 ? "rgba(15,91,216,0.05)" : "transparent",
                                }}
                              >
                                {entry ? entry.population : "—"}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Detail Modal */}
      <Dialog
  open={infoOpen}
  onClose={() => setInfoOpen(false)}
  maxWidth="xs"
  fullWidth
  PaperProps={{
    sx: {
      borderRadius: 4,
      p: SPACING.cardPadding,
      background: "linear-gradient(135deg, #f9fbff, #ffffff)",
      boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
    },
  }}
>
  <DialogTitle
    sx={{
      fontWeight: 700,
      color: "#0F5BD8",
      fontSize: "1.2rem",
    }}
  >
    Insight 💡
  </DialogTitle>

  <DialogContent>
    <Typography
      sx={{
        lineHeight: 1.7,
        color: "#374151",
        fontSize: "0.95rem",
      }}
    >
      {infoContent}
    </Typography>
  </DialogContent>

  <DialogActions>
    <Button
      onClick={() => setInfoOpen(false)}
      variant="contained"
      sx={{
        borderRadius: 2,
        textTransform: "none",
        px: 2,
      }}
    >
      Got it
    </Button>
  </DialogActions>
</Dialog>
    </Box>
  );
};

export default Comparison;
